import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import usePantallaEdit from "hooks/usePantallaEdit";
import { useRequestTracker } from "hooks/useResquestTracker";
import { MetodoPagoManager } from "managers";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { Simplebar } from "components/Simplebar/Simplebar";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import Select from "react-select";
import { MetodoPago } from "models/ECF/MetodoPago";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Exportar = lazy(() => import("components/Exportar/Exportar"));
const MetodoPagoEdit = lazy(() => import("./MetodoPagoEdit"));

const MetodoPagoList = ({ showLoading, hideLoading, hideTitle, isActive }) => {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const [showCanvas, setShowCanvas] = useState(false);
  const [metodoPago, setMetodoPagoState] = useState();
  const { Message, cerrarAlertas } = useMessage();
  const ref = useRef(null);
  const [showExportar, setShowExportar] = useState(false);
  const { trackRequest } = useRequestTracker();
  
  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    ...MetodoPago.ESTATUS_LIST,
  ];

  const METODO_PAGO_LIST = [
    { value: "", label: "Todos" },
    ...MetodoPago.METODO_PAGO_LIST,
  ];

  const {
    setEntidad: setMetodoPago,
    setEstadoCargado,
    setHayCambios,
  } = usePantallaEdit(setMetodoPagoState);

  useEffect(() => {
    if (isActive) {
      buscar();
    }
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  useEffect(() => {
    if (metodoPago != undefined) {
      setShowCanvas(true);
    }
  }, [metodoPago]);

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };

    trackRequest({
      executeRequest: (signal) =>
        MetodoPagoManager.listado(whereWithPaginationValues, {
          signal,
        }),
      onSuccess: (res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        for (let metodo of res.Data) {
          metodo["IndicadorDesc"] = MetodoPago.getMetodoPagoLabel(metodo["Indicador"])
        }
        setList(res.Data);
      },
      onError: (error) => handleException(error),
      onFinally: () => hideLoading(),
    });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (id) => {
    setEstadoCargado(false);
    if (id) {
      MetodoPagoManager.buscarPorId(id)
        .then((data) => {
          setMetodoPago(data);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          hideLoading();
          setEstadoCargado(true);
        });
    } else {
      setMetodoPago({});
      setEstadoCargado(true);
    }
  };

  const grabar = () => {
    ref.current?.showLoading(true);

    if (!metodoPago.IdSec) {
      MetodoPagoManager.crear(metodoPago)
        .then((res) => {
          Message({ message: `Grabado Exitosamente` });
          buscar();
          setMetodoPago(res);
          setShowCanvas(false);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          ref.current?.showLoading(false);
          setHayCambios(false);
        });
    } else {
      MetodoPagoManager.actualizar(metodoPago)
        .then(() => {
          buscar();
          Message({ message: `Actualizado Exitosamente` });
          setShowCanvas(false);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          ref.current?.showLoading(false);
          setHayCambios(false);
        });
    }
  };

  const metodoPagoEdit = (
    <Simplebar
      ref={ref}
      backdrop={"static"}
      confirmBack
      placement={"end"}
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      hideCallback={() => {
        setEstadoCargado(false);
      }}
      useLoading={true}
      title={
        metodoPago?.IdSec || false ? "Actualizar Forma de Pago" : "Crear Forma de Pago"
      }
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
    >
      <MetodoPagoEdit metodoPago={metodoPago} setMetodoPago={setMetodoPago} />
    </Simplebar>
  );

  const openExportar = () => {
    setShowExportar(true);
  };

  return (
    <div>
      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={MetodoPago.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            exportarCallBack={MetodoPagoManager.exportar}
            serialize={true}
          ></Exportar>
        </Suspense>
      )}

      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo={hideTitle ? "" : "Formas de Pago"}
          crearNuevo={() => handleClick()}
          top="0px"
          exportar={openExportar}
        ></ListHeader>
      </div>
      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>{metodoPagoEdit}</Suspense>
      )}
      <>
        <GridView
          pagination={pagination}
          setPagination={setPagination}
          allowPaging={pagination.EsPaginable}
          pageSize={pagination.CantidadPorPagina}
          pageIndex={pagination.IndiceDePagina}
          lastPageNumber={pagination.PaginasTotal}
          dataSource={list}
          onPageIndexChanging={onPageIndexChanging}
          where={where}
          setWhere={setWhere}
          height="70vh"
        >
          <GridView.Column
            isSticky
            bodyRenderer={(row) => {
              return (
                <>
                  <div className="float-right">
                    <IconButton onClick={() => handleClick(row.IdSec)}>
                      <ToolTip message="Editar">
                        <div>
                          <SvgComponent
                            iconName="pencil"
                            height="25"
                            width="25"
                          />
                        </div>
                      </ToolTip>
                    </IconButton>
                  </div>
                </>
              );
            }}
          />
          <GridView.Column
            headerText="Id"
            dataField="IdSec"
            sortExpression="IdSec"
            width="100px"
          />
          <GridView.Column
            headerText="Nombre"
            dataField="Nombre"
            sortExpression="Nombre"
          />
          <GridView.Column
            headerText="Tipo de Pago"
            dataField="IndicadorDesc"
            sortExpression="IndicadorDesc"
            filterRenderer={() => {
              return (
                <Select
                  className="react-select primary"
                  options={METODO_PAGO_LIST}
                  value={METODO_PAGO_LIST.filter(function (option) {
                    return (
                      option.value ===
                      (where.Indicador ? where.Indicador : METODO_PAGO_LIST[0].value)
                    );
                  })}
                  name="Indicador"
                  onChange={(e) =>
                    setWhere({
                      ...where,
                      Indicador: e.value,
                    })
                  }
                ></Select>
              );
            }}
          />
          <GridView.Column
            headerText="Estatus"
            dataField="Estatus"
            sortExpression="Estatus"
            filterRenderer={() => {
              return (
                <Select
                  className="react-select primary"
                  options={ESTATUS_LIST}
                  value={ESTATUS_LIST.filter(function (option) {
                    return (
                      option.value ===
                      (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                    );
                  })}
                  name="Estatus"
                  onChange={(e) =>
                    setWhere({
                      ...where,
                      Estatus: e.value,
                    })
                  }
                ></Select>
              );
            }}
          />
        </GridView>
      </>
    </div>
  );
};

export default MetodoPagoList;
